import { zodResolver } from "@hookform/resolvers/zod";
import { Form } from "@picklebet/ui";
import { Toggle } from "@picklebet/ui";
import React from "react";
import { useForm } from "react-hook-form";
import type { NullableMaybe } from "types/utilities";
import { z } from "zod";
import type { ProcessedEntry } from "@/hooks/firestore/betting/useEntries";
import { ShareEntryPreview } from "./ShareEntryPreview";

import * as styles from "./ShareEntryForm.module.scss";

const schema = z.object({
  title: z.string().min(1),
  displayStake: z.boolean(),
});

export type ShareEntryFormSchema = z.infer<typeof schema>;

type ShareEntryFormProps = {
  username: string;
  onSubmit: (values: ShareEntryFormSchema) => Promise<void>;
  onFieldTouched: () => void;
  submitButton: (loading: boolean) => React.ReactNode;
  entry?: [NullableMaybe<ProcessedEntry>, boolean];
};

export const ShareEntryForm = ({
  username,
  onSubmit,
  onFieldTouched,
  submitButton,
  ...props
}: ShareEntryFormProps) => {
  const [entry, isPreviewLoading] = props.entry || [undefined, false];

  const {
    handleSubmit,
    register,
    setValue,
    watch,
    formState: { isSubmitting },
  } = useForm<ShareEntryFormSchema>({
    resolver: zodResolver(schema),
    defaultValues: {
      title: `${username}'s shared bet`,
      displayStake: true,
    },
  });

  const handleInputChange = (
    field: keyof ShareEntryFormSchema,
    value: ShareEntryFormSchema[keyof ShareEntryFormSchema],
  ) => {
    onFieldTouched?.();
    setValue(field, value);
  };

  return (
    <Form
      noSpacing
      className={styles.shareForm}
      onSubmit={handleSubmit(onSubmit)}
    >
      {!!entry && (
        <ShareEntryPreview
          entry={{
            ...entry,
            hideStake: !watch("displayStake"),
          }}
          isLoading={isPreviewLoading}
        />
      )}

      <Toggle
        id="displayStake"
        {...register("displayStake")}
        label="Display stake information"
        disabled={isSubmitting}
        checked={watch("displayStake")}
        onCheckedChange={(checked) =>
          handleInputChange("displayStake", checked)
        }
        className={styles.toggle}
        labelRight
        noSpacing
      />
      <div>{submitButton(isSubmitting)}</div>
    </Form>
  );
};
